export const ALL_SETTINGS_TAB_IDS = [
  'profile',
  'preferences',
  'company',
  'users',
  'mappings',
  'price-generator',
  'access-configurator',
  'loss-reasons',
  'sub-pipelines',
  'route-generator',
  'variables',
  'email-and-docs',
  'google-contacts',
  'audit-log',
  'error-logs',
] as const;

export type SettingsTab = (typeof ALL_SETTINGS_TAB_IDS)[number];

export const VALID_VARIABLES_SUBTABS = ['product-types', 'manuals-hierarchy', 'image-lookups'] as const;
export type VariablesSubTab = (typeof VALID_VARIABLES_SUBTABS)[number];

export const VALID_EMAIL_DOCS_SUBTABS = ['email-templates', 'email-accounts', 'contract-templates'] as const;
export type EmailDocsSubTab = (typeof VALID_EMAIL_DOCS_SUBTABS)[number];

export type MenuLabelKey = `settings.${string}`;

export type SettingsNavMenuItem = { value: SettingsTab; labelKey: MenuLabelKey };

export type SettingsMenuGroup = {
  id: 'account' | 'crm' | 'integrations' | 'communication' | 'content' | 'admin';
  labelKey: MenuLabelKey;
  items: SettingsNavMenuItem[];
};

export const VARIABLES_SUB_ITEMS: {
  value: VariablesSubTab;
  labelKey: 'settings.productTypes' | 'settings.manualsHierarchy' | 'settings.imageLookups';
}[] = [
  { value: 'product-types', labelKey: 'settings.productTypes' },
  { value: 'manuals-hierarchy', labelKey: 'settings.manualsHierarchy' },
  { value: 'image-lookups', labelKey: 'settings.imageLookups' },
];

export const EMAIL_DOCS_SUB_ITEMS: {
  value: EmailDocsSubTab;
  labelKey: 'settings.tabEmailTemplates' | 'settings.tabEmailAccounts' | 'settings.tabContractTemplates';
}[] = [
  { value: 'email-templates', labelKey: 'settings.tabEmailTemplates' },
  { value: 'email-accounts', labelKey: 'settings.tabEmailAccounts' },
  { value: 'contract-templates', labelKey: 'settings.tabContractTemplates' },
];

export function settingsNavItemMatchesFilter(
  item: SettingsNavMenuItem,
  navFilterLower: string,
  labelMatches: (key: string) => boolean
): boolean {
  if (item.value === 'variables') {
    return (
      labelMatches('settings.catalogAndContent') ||
      VARIABLES_SUB_ITEMS.some((s) => labelMatches(s.labelKey)) ||
      !navFilterLower
    );
  }
  if (item.value === 'email-and-docs') {
    return (
      labelMatches('settings.emailAndDocs') ||
      EMAIL_DOCS_SUB_ITEMS.some((s) => labelMatches(s.labelKey)) ||
      !navFilterLower
    );
  }
  return labelMatches(item.labelKey);
}

export function buildSettingsMenuGroups(isAdmin: boolean): SettingsMenuGroup[] {
  const accountItems: SettingsNavMenuItem[] = [
    { value: 'profile', labelKey: 'settings.profile' },
    { value: 'preferences', labelKey: 'settings.preferences' },
  ];
  if (!isAdmin) {
    return [{ id: 'account', labelKey: 'settings.menuGroupAccount', items: accountItems }];
  }
  return [
    { id: 'account', labelKey: 'settings.menuGroupAccount', items: accountItems },
    {
      id: 'crm',
      labelKey: 'settings.menuGroupCrm',
      items: [
        { value: 'company', labelKey: 'settings.companyMenuLabel' },
        { value: 'sub-pipelines', labelKey: 'settings.subPipelinesMenuLabel' },
        { value: 'route-generator', labelKey: 'settings.routeGenerator' },
      ],
    },
    {
      id: 'integrations',
      labelKey: 'settings.menuGroupIntegrations',
      items: [
        { value: 'mappings', labelKey: 'settings.mappings' },
        { value: 'price-generator', labelKey: 'settings.priceGenerator' },
        { value: 'access-configurator', labelKey: 'settings.accessConfigurator' },
        { value: 'google-contacts', labelKey: 'settings.googleContactsSync' },
      ],
    },
    {
      id: 'communication',
      labelKey: 'settings.menuGroupCommunication',
      items: [{ value: 'email-and-docs', labelKey: 'settings.emailAndDocs' }],
    },
    {
      id: 'content',
      labelKey: 'settings.menuGroupContent',
      items: [{ value: 'variables', labelKey: 'settings.catalogAndContent' }],
    },
    {
      id: 'admin',
      labelKey: 'settings.menuGroupAdmin',
      items: [
        { value: 'users', labelKey: 'settings.users' },
        { value: 'audit-log', labelKey: 'settings.auditLog' },
        { value: 'error-logs', labelKey: 'settings.errorLogs' },
        { value: 'loss-reasons', labelKey: 'settings.lossReasons' },
      ],
    },
  ];
}

export const SETTINGS_TAB_SR_KEYS: Record<SettingsTab, MenuLabelKey> = {
  profile: 'settings.profile',
  preferences: 'settings.preferences',
  company: 'settings.companyMenuLabel',
  users: 'settings.users',
  mappings: 'settings.mappings',
  'price-generator': 'settings.priceGenerator',
  'access-configurator': 'settings.accessConfigurator',
  'loss-reasons': 'settings.lossReasons',
  'sub-pipelines': 'settings.subPipelinesMenuLabel',
  'route-generator': 'settings.routeGenerator',
  variables: 'settings.catalogAndContent',
  'email-and-docs': 'settings.emailAndDocs',
  'google-contacts': 'settings.googleContactsSync',
  'audit-log': 'settings.auditLog',
  'error-logs': 'settings.errorLogs',
};

export function buildSettingsPath(
  tab: SettingsTab,
  variablesSub: VariablesSubTab,
  emailDocsSub: EmailDocsSubTab
): string {
  if (tab === 'variables') return `/settings/variables/${variablesSub}`;
  if (tab === 'email-and-docs') return `/settings/email-and-docs/${emailDocsSub}`;
  return `/settings/${tab}`;
}
